import {
    type AppRoute,
    type ClientInferResponseBody,
    type ClientInferResponses,
    initContract,
} from '@ts-rest/core'
import { initQueryClient, useTsRestQueryClient } from '@ts-rest/react-query'
import { z } from 'zod'

import { UserLocation } from '~/common/schemas/location'
import { ShopeeLogisticsProvider } from '~/common/schemas/logistics-shopee'
import { ChannelStockUnitDiscount, Product, ProductDiscount, ProductNote } from '~/common/schemas/product'
import type { Shop } from '~/common/schemas/shop'
import { BaseStockItem, StockRecord, StockRecordAdjustments } from '~/common/schemas/stock'
import { createPromotionContract } from '~/promotions/contract'
import { SaleNote } from '~/sales/schema'
import type {
    FieldInfo,
    InitShippingInfo,
    PaginatedParameters,
    PaginatedResponse,
    SalesFilter,
} from '.'
import { makePaginatedResponse } from './helper'
import { createAppClientOptions } from './utils'

const permissionSchema = z.object({
    id: z.number(),
    name: z.string(),
    codename: z.string(),
    permission_type: z.enum(['api', 'ui']),
    object_pk: z.coerce.number().nullish(),
    content_type: z
        .object({
            id: z.number(),
            app_label: z.string(),
            model: z.string(),
        })
        .nullish(),
})

export const api = initQueryClient(createApiContract(), createAppClientOptions())

function createApiContract() {
    const c = initContract()

    return c.router(
        {
            users: createUserContract(),
            products: createProductContract(),
            sales: createSaleContract(),
            groups: createGroupContract(),
            channels: createChannelContract(),
            integrations: createIntegrationsContract(),
            stock: createStockContract(),
            stores: createStoreContract(),
            customers: createCustomerContract(),
            addresses: createAddressContract(),
            discounts: createDiscountContract(),
            promotions: createPromotionContract(),
            conversations: createConversationsContract(),
        },
        { strictStatusCodes: true }
    )
}
export type ApiContract = ReturnType<typeof createApiContract>

function createUserContract() {
    const basicUserSchema = z.object({
        id: z.number(),
        account_owner: z.number(),
        username: z.string(),
        first_name: z.string(),
        last_name: z.string(),
        email: z.string(),
        groups: z.array(z.number()),
        is_account_owner: z.boolean(),
    })

    const c = initContract()

    return c.router({
        accountUserListing: {
            method: 'GET',
            path: '/users/:id/users/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Get users managed by the current account owner',
            responses: {
                200: z.array(basicUserSchema),
                403: makeGenericError(),
            },
        },
        accountGroupListing: {
            method: 'GET',
            path: '/users/:id/groups/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Get groups managed by the current account owner',
            responses: {
                200: z.array(
                    z.object({
                        id: z.number(),
                        is_active: z.boolean(),
                        is_admin: z.boolean(),
                        members: z.array(
                            basicUserSchema.pick({ id: true, email: true }).extend({
                                first_name: z.string().nullable(),
                                last_name: z.string().nullable(),
                                username: z.string().nullable(),
                                is_demo: z.boolean(),
                            })
                        ),
                        name: z.string(),
                        owner: z.number(),
                        permissions: z.array(z.number()),
                    })
                ),
            },
        },
        locations: {
            method: 'GET',
            path: '/users/:id/locations/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Get user fulfillment locations',
            responses: {
                200: UserLocation.array(),
            },
        },
        create: {
            method: 'POST',
            path: '/users/create/',
            summary: 'Create user',
            body: basicUserSchema.omit({ id: true, is_account_owner: true }),
            responses: {
                201: z.object({
                    password: z.string(),
                    user: basicUserSchema,
                }),
                400: makeGenericError(z.record(z.array(z.string()))),
            },
        },
        update: {
            method: 'PUT',
            path: '/users/:id/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Update user',
            body: basicUserSchema,
            responses: {
                200: basicUserSchema,
                400: makeGenericError(z.record(z.array(z.string()))),
            },
        },
        delete: {
            method: 'DELETE',
            path: '/users/:id/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Delete user',
            body: z.never(),
            responses: {
                204: z.never(),
            },
        },
        permissions: {
            method: 'GET',
            path: '/users/:id/permissions/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Get user permissions',
            responses: {
                200: z.array(permissionSchema),
            },
        },
        updatePermissions: {
            method: 'POST',
            path: '/users/:id/permissions/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Update user permissions',
            body: z.array(permissionSchema),
            responses: {
                200: z.array(permissionSchema),
            },
        },
        products: {
            method: 'GET',
            path: '/users/:id/products/',
            pathParams: c.type<{ id: number }>(),
            query: c.type<
                PaginatedParameters & {
                    status?: string
                    search?: string
                    channel_products__shop?: number
                    channel_products__shop__exclusive?: boolean
                }
            >(),
            summary: 'List user products',
            responses: {
                200: c.type<PaginatedResponse<Product>>(),
            },
        },
        stockItems: {
            method: 'GET',
            path: '/users/:accountOwnerId/stock-items/',
            pathParams: c.type<{ accountOwnerId: number }>(),
            summary: 'List stock items of an account owner',
            query: c.type<
                PaginatedParameters & {
                    search?: string
                    is_bundle?: boolean
                    show_reserved_stock_issues?: boolean
                }
            >(),
            responses: {
                200: c.type<
                    PaginatedResponse<{
                        barcode: string | null
                        buffer_threshold: number
                        id: number
                        is_bundle: boolean
                        is_bundle_member: boolean
                        name: string
                        sku: string
                        reserved_stock: number
                        total_quantity: number
                        stock_records: Array<{
                            id: number
                            location: number
                            quantity: number
                        }>
                    }>
                >(),
            },
        },
    })
}

function createProductContract() {
    const c = initContract()

    return c.router({
        list: {
            method: 'GET',
            path: '/products/',
            summary: 'Get products',
            query: c.type<{ search?: string }>(),
            responses: {
                200: c.type<PaginatedResponse<Product>>(),
            },
        },
        detail: {
            method: 'GET',
            path: '/products/:id/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Get product detail',
            validateResponseOnClient: true,
            responses: {
                200: Product,
            },
        },
        stockRecords: {
            method: 'GET',
            path: '/products/:id/stock-records/',
            pathParams: z.object({ id: z.number() }),
            query: z.object({ include_bundle_items: z.boolean().optional() }),
            summary: 'Get stock records of a product',
            responses: {
                200: z.array(StockRecord),
            },
        },
        discounts: {
            method: 'GET',
            path: '/products/:id/product-discounts/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Get product discounts',
            responses: {
                200: z.array(ProductDiscount),
            },
        },
        activeChannelStockUnitDiscounts: {
            method: 'GET',
            path: '/products/:id/active-channel-stock-unit-discounts/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Get active channel stock unit discounts',
            responses: {
                200: z.array(ChannelStockUnitDiscount),
            },
        },
        /*
         * TODO: Add create-update-delete methods
         * NOTE: Do not use yet, still in development
         * */
        create: {
            method: 'POST',
            path: '/products/',
            summary: 'Create a product',
            body: Product,
            responses: {
                201: z.object({ id: z.number() }),
            },
        },
        updateStatus: {
            method: 'PATCH',
            path: '/products/:id/status/',
            summary: 'Update product status',
            pathParams: z.object({ id: z.number() }),
            body: z.object({
                status: z.enum(['active', 'inactive']),
            }),
            responses: {
                200: z.object({ id: z.number() }),
            },
        },
        notes: {
            method: 'GET',
            path: '/products/:productId/notes/',
            pathParams: z.object({ productId: z.number() }),
            summary: 'Get notes',
            responses: {
                200: ProductNote.array(),
            },
        },
        createNote: {
            method: 'POST',
            path: '/products/:productId/notes/',
            pathParams: z.object({ productId: z.number() }),
            summary: 'Create note',
            body: ProductNote.partial(),
            responses: {
                201: ProductNote,
            },
        },
        updateNote: {
            method: 'PUT',
            path: '/products/:productId/notes/:noteId/',
            pathParams: z.object({ productId: z.number(), noteId: z.number() }),
            summary: 'Update note',
            body: ProductNote.partial(),
            responses: {
                200: ProductNote,
            },
        },
        deleteNote: {
            method: 'DELETE',
            path: '/products/:productId/notes/:noteId/',
            pathParams: z.object({ productId: z.number(), noteId: z.number() }),
            summary: 'Delete note',
            body: z.never(),
            responses: {
                204: z.never(),
            },
        },
    })
}

function createSaleContract() {
    const c = initContract()

    return c.router({
        initShippingInfoFields: {
            method: 'POST',
            path: '/sales/init-shipping-info/fields/',
            summary: 'Get init shipping info fields',
            body: c.type<{ sales_filter: Pick<SalesFilter, 'included_sale_pks'> }>(),
            responses: {
                200: c.type<
                    Array<{
                        shop: Shop | null
                        field_info: FieldInfo
                        init_shipping_info: InitShippingInfo
                    }>
                >(),
            },
        },
        ids: {
            method: 'GET',
            path: '/users/:userId/sales/pks/',
            summary: 'Get sale IDs',
            pathParams: z.object({ userId: z.number() }),
            query: c.type<
                Partial<{
                    date_created_after: string
                    date_created_before: string
                    status: string
                    shop__channel: number
                    shop: number
                }>
            >(),
            responses: {
                200: z.array(z.number()),
            },
        },
        filterValues: {
            method: 'GET',
            path: '/users/:userId/filter-values/sales/',
            pathParams: z.object({ userId: z.number() }),
            summary: 'Get sales filter values',
            responses: {
                200: c.type<
                    Array<{
                        field: string
                        lookup: string
                        select_multiple: boolean
                        values: Array<{ value: string; label: string }>
                    }>
                >(),
            },
        },
        initShippingInfo: {
            method: 'POST',
            path: '/sales/init-shipping-info/',
            summary: 'Init shipping info',
            body: c.type<{
                // biome-ignore lint/suspicious/noExplicitAny: TODO: Update this type
                init_shipping_info: Record<number, Record<string, any>>
                sales_filter: Partial<SalesFilter>
            }>(),
            responses: {
                201: c.type<{
                    status: string
                    pick_pack_batch_id: number | null
                    errors: Array<{
                        message: string
                        fulfillment_pk: number
                        sale_pk: number
                        sale_id: number
                    }>
                }>(),
            },
        },
        notes: {
            method: 'GET',
            path: '/sales/:saleId/notes/',
            pathParams: z.object({ saleId: z.number() }),
            summary: 'Get sale notes',
            responses: {
                200: SaleNote.array(),
            },
        },
        createNote: {
            method: 'POST',
            path: '/sales/:saleId/notes/',
            pathParams: z.object({ saleId: z.number() }),
            summary: 'Create sale note',
            body: SaleNote.partial(),
            responses: {
                201: SaleNote,
            },
        },
        updateNote: {
            method: 'PUT',
            path: '/sales/:saleId/notes/:noteId/',
            pathParams: z.object({ saleId: z.number(), noteId: z.number() }),
            summary: 'Update sale note',
            body: SaleNote.partial(),
            responses: {
                200: SaleNote,
            },
        },
        deleteNote: {
            method: 'DELETE',
            path: '/sales/:saleId/notes/:noteId/',
            pathParams: z.object({ saleId: z.number(), noteId: z.number() }),
            summary: 'Delete sale note',
            body: z.never(),
            responses: {
                204: z.never(),
            },
        },
    })
}

function createGroupContract() {
    const c = initContract()

    return c.router({
        detail: {
            method: 'GET',
            path: '/groups/:id/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Get group detail',
            responses: {
                200: z.object({
                    id: z.number(),
                    name: z.string(),
                    is_active: z.boolean(),
                    is_admin: z.boolean(),
                    owner: z.number(),
                    permissions: z.array(z.number()),
                    members: z.array(
                        z.object({
                            id: z.number(),
                            email: z.string(),
                            username: z.string().nullable(),
                            first_name: z.string().nullable(),
                            last_name: z.string().nullable(),
                        })
                    ),
                }),
            },
        },
        permissions: {
            method: 'GET',
            path: '/groups/:id/permissions/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Get group permissions',
            responses: {
                200: z.array(permissionSchema),
            },
        },
        addUsers: {
            method: 'POST',
            path: '/groups/:id/users/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Add users to group',
            body: z.object({ users: z.array(z.number()) }),
            responses: {
                200: z.object({ id: z.number() }),
            },
        },
        removeUsers: {
            method: 'DELETE',
            path: '/groups/:id/users/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Remove users from group',
            body: z.object({ users: z.array(z.number()) }),
            responses: {
                204: z.never(),
            },
        },
    })
}

function createIntegrationsContract() {
    const c = initContract()
    const SettingResponse = z
        .object({
            ouCode: z.string(),
            source: z.string(),
            branch_code: z.string(),
            pos_api_url: z.string(),
            master_api_url: z.string(),
            sale_person_code: z.string(),
            sync_sales_to_system: z.boolean(),
            retail_price_list_code: z.string(),
            retail_price_list_keyword: z.string(),
            marketplace_price_list_code: z.string(),
            sync_stock_from_system_status: z.string(),
            marketplace_price_list_keyword: z.string(),
            sync_stock_from_system_message: z.union([z.string(), z.null()]),
            update_price_from_system_to_channels: z.boolean(),
            sync_stock_from_system_last_updated_time: z.string(),
            price_lists: z.array(
                z.object({
                    pricelistCode: z.string(),
                    pricelistDesc: z.string(),
                    startDate: z.string(),
                    endDate: z.nullable(z.string()),
                    shopCondition: z.string(),
                    custCondition: z.string(),
                    upddateBy: z.string(),
                    upddateDate: z.string(),
                })
            ),
        })
        .passthrough()

    return {
        jaymart: c.router({
            settings: {
                method: 'GET',
                path: '/integrations/jaymart/settings/',
                summary: 'Get Jaymart settings',
                responses: {
                    200: SettingResponse,
                },
            },
            updateSetting: {
                method: 'POST',
                path: '/integrations/jaymart/settings/',
                summary: 'Update Jaymart settings',
                body: z.object({
                    settings: z.record(
                        z.enum([
                            'retail_price_list_code',
                            'marketplace_price_list_code',
                            'branch_code',
                        ]),
                        z.string()
                    ),
                }),
                responses: {
                    200: SettingResponse,
                },
            },
            syncStock: {
                method: 'PUT',
                path: '/integrations/jaymart/sync-stock/',
                summary: 'Sync Jaymart stock',
                body: z.never(),
                responses: {
                    200: SettingResponse,
                },
            },
        }),
        rainforest: c.router({
            settingsByName: {
                method: 'GET',
                path: '/integrations/rainforest/:name/settings/',
                pathParams: z.object({ name: z.enum(['sandbox', 'production']) }),
                summary: 'Get Rainforest settings by environment name',
                responses: {
                    200: c.type<{
                        settings: Record<string, unknown>
                        shops: Array<{ id: number; shop_name: string }>
                        subsidiaries?: unknown
                        customers?: unknown
                        locations?: unknown
                        items?: unknown
                        accounts?: unknown
                        log_history?: Array<{ timestamp: number; connection?: string; module?: string; log_message?: string }>
                    }>(),
                },
            },
            updateSettingsByName: {
                method: 'POST',
                path: '/integrations/rainforest/:name/settings/',
                pathParams: z.object({ name: z.enum(['sandbox', 'production']) }),
                summary: 'Update Rainforest settings by environment name',
                body: c.type<{ settings: Record<string, unknown> }>(),
                responses: {
                    200: c.type<{ settings: Record<string, unknown> }>(),
                },
            },
        }),
    
}

}

function createStockContract() {
    const c = initContract()

    return c.router({
        stockRecords: {
            method: 'GET',
            path: '/stock/stock-records/',
            query: c.type<
                PaginatedParameters & {
                    search?: string
                    is_bundle?: boolean
                    show_reserved_stock_issues?: boolean
                }
            >(),
            summary: 'Get stock records',
            responses: {
                200: c.type<
                    PaginatedResponse<{
                        barcode: string | null
                        buffer_threshold: number
                        id: number
                        is_bundle: boolean
                        is_bundle_member: boolean
                        name: string
                        sku: string
                        reserved_stock: number
                        stock_records: Array<{
                            id: number
                            location_id: number
                            quantity: number
                        }>
                    }>
                >(),
            },
        },
        stockRecordAdjustments: {
            method: 'GET',
            path: '/stock/stock-records/:id/adjustments',
            pathParams: z.object({ id: z.number() }),
            query: z.object({
                search: z.string().optional(),
                user_id: z.number().optional(),
                adjustment_type: z.string().optional(),
                date_updated_before: z.string().optional(),
                date_updated_after: z.string().optional(),
                order_by: z.string().optional(),
            }),
            summary: 'Get stock record adjustments',
            responses: {
                200: z.array(StockRecordAdjustments),
            },
        },
        stockRecordAdjustmentFilterValues: {
            method: 'GET',
            path: '/stock/stock-records/:id/adjustments/filter-values',
            pathParams: z.object({ id: z.number() }),
            summary: 'Get stock record adjustments',
            responses: {
                200: z.array(
                    z.object({
                        field: z.string(),
                        lookup: z.string(),
                        select_multiple: z.boolean(),
                        values: z.array(
                            z.object({
                                label: z.string(),
                                value: z.string(),
                            })
                        ),
                    })
                ),
            },
        },
        stockItems: {
            method: 'GET',
            path: '/stock/stock-items/',
            query: z
                .object({
                    search: z.string(),
                    id_not_in: z.string(),
                })
                .partial(),
            summary: 'Get stock items',
            responses: {
                200: c.type<
                    PaginatedResponse<{
                        id: number
                        name: string | null
                        cover_image_url: string | null
                    }>
                >(),
            },
        },
        stockItem: {
            method: 'GET',
            path: '/stock/stock-items/:id/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Get a stock item detail, included bundle items',
            responses: {
                200: BaseStockItem,
            },
        },
        updateStockItem: {
            method: 'PATCH',
            path: '/stock/stock-items/:id/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Update stock item',
            body: z.object({ users: z.array(z.number()) }),
            responses: {
                200: z.object({ id: z.number() }),
            },
        },
        stockItemsWithStock: {
            method: 'GET',
            path: '/channels/stock/stock-items/',
            query: z
                .object({
                    search: z.string(),
                    is_active: z.boolean(),
                    limit: z.number(),
                    offset: z.number(),
                    is_bundle: z.string(),
                })
                .partial(),
            responses: {
                200: makePaginatedResponse(
                    z.object({
                        id: z.number(),
                        sku: z.string(),
                        name: z.string(),
                        is_bundle: z.boolean(),
                        stocks: z.array(
                            z.object({
                                channel_stock_item_id: z.number(),
                                shop_id: z.number(),
                                channel_id: z.number(),
                                channel_name: z.string(),
                                actual_stock: z.number(),
                                calculated_stock: z.number(),
                            })
                        ),
                    })
                ),
            },
        },
        channelStockItemBreakdown: {
            method: 'GET',
            path: '/channels/stock/channel-stock-items/:id/',
            pathParams: c.type<{ id: number }>(),
            responses: {
                200: c.type<{
                    id: number
                    shop_id: number
                    stock_item_id: number
                    actual_stock: number
                    buffer_adjustment: number
                    buffer_threshold: number
                    stock_adjustment: number
                    reserved_stock: number
                    calculated_stock: number
                    active_channel_stock_adjustment: {
                        adjustment_group: {
                            id: number
                            name: string
                            store: number
                            status: string
                            is_active: boolean
                            start_date: string
                            end_date: string
                        }
                    } | null
                    active_channel_stock_item_campaign_locks: Array<{
                        units_allocated: number
                        units_remaining: number
                        channel_stock_item: number
                        shop_location: number | null
                        shop_name: string
                        channel_name: string
                        channel_campaign: {
                            channel_campaign_id: string
                            campaign_type: string
                            start_date: string
                            end_date: string
                        }
                    }>
                    stock_records: Array<{
                        id: number
                        quantity: number
                        quantity_allocated: number
                        depletion_velocity: number
                        location: number
                        stock_item: number
                    }>
                    bundle_stock_items: Array<{
                        quantity: number
                        stock_item: {
                            id: number
                            sku: string
                            name: string
                            num_of_units: number | null
                        }
                    }>
                }>(),
            },
        },
    })
}

function createChannelContract() {
    const channelSchema = z.object({
        id: z.number(),
        channel_icon_url: z.string(),
        channel_square_icon_url: z.string(),
        name: z.string(),
        country_code: z.string().nullable(),
        selectable: z.boolean(),
        has_categories: z.boolean(),
        has_category_attributes: z.boolean(),
        categories_are_shop_level: z.boolean(),
        has_channel_manager: z.boolean(),
        has_integrated_shipping: z.boolean(),
        allow_duplicate_skus: z.boolean(),
    })
    const categorySchema = z.object({
        id: z.number(),
        translations: z.record(z.object({ name: z.string() })),
        category_id: z.number(),
        channel: z.number(),
        is_leaf: z.boolean(),
        depth: z.number(),
        updating: z.boolean(),
        supports_multiple_stock_units: z.boolean(),
    })
    const attributeSchema = z.object({
        id: z.number(),
        name: z.string(),
        tip: z.string().nullable(),
        attribute_id: z.string(),
        attribute_type: z.enum([
            'text',
            'integer',
            'float',
            'richtext',
            'date',
            'datetime',
            'option',
            'multi_option',
            'image',
            'json',
        ]),
        options: z.array(
            z.object({
                label: z.string(),
                value: z.string(),
                inactive: z.boolean(),
            })
        ),
        units: z.array(z.object({ id: z.number(), name: z.string() })),
        required: z.boolean(),
        can_create_option: z.boolean(),
        should_fetch_options_async: z.boolean(),
        sku_attribute: z.boolean(),
        unique_for_stock_unit: z.boolean(),
        recommended: z.boolean(),
        is_shop_specific: z.boolean(),
    })
    const attributeOptionValueSchema = z.object({
        value: z.nullable(z.string()),
        label: z.string(),
        inactive: z.boolean(),
    })

    const c = initContract()

    return c.router({
        list: {
            method: 'GET',
            path: '/channels/',
            summary: 'List all channels',
            responses: {
                200: z.array(channelSchema),
            },
        },
        detail: {
            method: 'GET',
            path: '/channels/:id/',
            pathParams: z.object({ id: z.number() }),
            summary: 'Get channel detail by id',
            responses: {
                200: channelSchema,
            },
        },
        categoryDetail: {
            method: 'GET',
            path: '/channels/:channelId/category/:categoryId',
            pathParams: z.object({
                channelId: z.number(),
                categoryId: z.string(), // String is required because 0 is falsy
            }),
            summary: 'Get category detail in a channel',
            responses: {
                200: categorySchema,
            },
        },
        categoryAncestors: {
            method: 'GET',
            path: '/channels/:channelId/category/:categoryId/ancestors/',
            pathParams: z.object({
                channelId: z.number(),
                categoryId: z.number(), // Number can be used here since it should never be 0
            }),
            summary: 'Get ancestor categories of a category in a channel',
            responses: {
                200: z.array(categorySchema.extend({ children: z.array(categorySchema) })),
            },
        },
        categoryChildren: {
            method: 'GET',
            path: '/channels/:channelId/category/:categoryId/children/',
            pathParams: c.type<{
                channelId: number
                categoryId: string
            }>(),
            query: c.type<{
                shop_id?: string
                with_products_only?: boolean
                search?: string
            }>(),
            summary: 'Get category children in a channel',
            responses: {
                200: c.type<{
                    count: number
                    next: string | null
                    previous: string | null
                    results: z.infer<typeof categorySchema>[]
                }>(),
            },
        },
        categoryAttributes: {
            method: 'GET',
            path: '/channels/:channelId/category/:categoryId/attributes/:language/',
            pathParams: z.object({
                channelId: z.number(),
                categoryId: z.string(), // String is required because the API considers 0 as falsy
                language: z.string(),
            }),
            query: z.object({
                shop_id: z.number(),
            }),
            summary: 'Get category attributes in a channel',
            responses: {
                200: z.array(attributeSchema),
                403: makeGenericError(),
            },
        },
        attributeOptions: {
            method: 'GET',
            path: '/channels/products/attributes/:attributePk/options/:language/',
            pathParams: c.type<{
                attributePk: number
                language: string
            }>(),
            query: c.type<{
                category_id: number
                search: string
            }>(),
            summary: 'Get category attributes in a channel',
            responses: {
                200: c.type<Array<{ label: string; value: string; inactive: boolean }>>(),
            },
        },
        attributeValues: {
            method: 'GET',
            path: '/channels/products/:channelProductId/attribute/values/',
            pathParams: z.object({
                channelProductId: z.number(),
            }),
            query: c.type<{ language: string }>(),
            summary:
                'Get existing product attribute values and stock unit attribute values of a channel product',
            responses: {
                200: z.object({
                    products: z.array(
                        z.object({
                            id: z.number(),
                            attribute: z.object({
                                attribute_id: z.string(),
                                id: z.number(),
                            }),
                            channel_product: z.number(),
                            unit: z.object({ id: z.number(), name: z.string() }).nullable(),
                            value: z.union([
                                z.string().describe('Value'),
                                attributeOptionValueSchema,
                                attributeOptionValueSchema.array(),
                                ShopeeLogisticsProvider.array(),
                            ]),
                        })
                    ),
                    stock_units: z.array(
                        z.object({
                            attribute: z.object({
                                attribute_id: z.string(),
                                id: z.number(),
                            }),
                            channel_stock_unit: z.number(),
                            id: z.number(),
                            unit: z.number().nullable(),
                            value: z.union([
                                z.string().describe('Value'),
                                attributeOptionValueSchema,
                            ]),
                        })
                    ),
                }),
            },
        },
        customers: {
            method: 'GET',
            path: '/channels/customers/',
            query: c.type<{ search: string }>(),
            summary: 'Get channel customers',
            responses: {
                200: c.type<
                    Array<{
                        id: number
                        channel_customer_id: string
                        customer: {
                            name: string
                            id: number
                        }
                        shop: {
                            id: number
                            shop_name: string
                            channel: {
                                id: number
                                name: string
                                channel_icon_url: string
                                channel_square_icon_url: string
                            }
                        }
                        sale_ids: string[]
                    }>
                >(),
            },
        },
        channelCustomerDetail: {
            method: 'GET',
            path: '/channels/customers/id/:channelCustomerId/',
            pathParams: c.type<{ channelCustomerId: string }>(),
            summary: 'Get channel customer detail',
            responses: {
                200: c.type<{
                    id: number
                    channel_customer_id: string
                    customer: number
                }>(),
                404: makeGenericError(),
            },
        },
        channelProductDetail: {
            method: 'GET',
            path: '/channels/products/:channelProductPk/',
            pathParams: z.object({ channelProductPk: z.number() }),
            summary: 'Get channel product detail',
            responses: {
                200: c.type<{
                    id: number
                    product: number
                }>(),
            },
        },
    })
}

function createStoreContract() {
    const c = initContract()

    return c.router({
        getPresignedUrl: {
            method: 'POST',
            path: '/stores/:storeId/upload/signature/',
            pathParams: c.type<{ storeId: number }>(),
            summary: 'Get presigned url for upload',
            body: c.type<Array<{ filename: string; content_type: string }>>(),
            responses: {
                200: c.type<
                    Array<{ presigned_url: string; original_filename: string; filename: string }>
                >(),
            },
        },
        shippingProviders: {
            method: 'GET',
            path: '/stores/:storeId/shipping-providers/',
            pathParams: c.type<{ storeId: number }>(),
            summary: 'Get shipping providers for a store',
            responses: {
                200: c.type<
                    PaginatedResponse<{
                        id: number
                        shipping_provider_id: string
                        logo_url: string
                        name: string
                        can_get_quote: boolean
                        can_cancel: boolean
                        can_update: boolean
                    }>
                >(),
            },
        },
        notes: {
            method: 'GET',
            path: '/stores/:storeId/notes/',
            pathParams: z.object({ storeId: z.number() }),
            summary: 'Get notes',
            responses: {
                200: ProductNote.array(),
            },
        },
        createNote: {
            method: 'POST',
            path: '/stores/:storeId/notes/',
            pathParams: z.object({ storeId: z.number() }),
            summary: 'Create note',
            body: ProductNote.partial(),
            responses: {
                201: ProductNote,
            },
        },
        updateNote: {
            method: 'PUT',
            path: '/stores/:storeId/notes/:noteId/',
            pathParams: z.object({ storeId: z.number(), noteId: z.number() }),
            summary: 'Update note',
            body: ProductNote.partial(),
            responses: {
                200: ProductNote,
            },
        },
        deleteNote: {
            method: 'DELETE',
            path: '/stores/:storeId/notes/:noteId/',
            pathParams: z.object({ storeId: z.number(), noteId: z.number() }),
            summary: 'Delete note',
            body: z.never(),
            responses: {
                204: z.never(),
            },
        },
    })
}

function createCustomerContract() {
    const c = initContract()

    return c.router({
        detail: {
            method: 'GET',
            path: '/customers/:customerId/',
            pathParams: c.type<{ customerId: number }>(),
            summary: 'Get detail of 1 customer',
            responses: {
                200: c.type<{
                    id: number
                    tax_id: string | null
                    phone: string | null
                    email: string
                    default_shipping_address: number | null
                    default_billing_address: number | null
                    default_invoice_address: number | null
                }>(),
            },
        },
        stats: {
            method: 'GET',
            path: '/customers/:customerId/stats/',
            summary: 'Get stats of 1 customer',
            responses: {
                200: c.type<{
                    sales_count: number
                    total_sales_value: number
                }>(),
            },
        },
        sales: {
            method: 'GET',
            path: '/customers/:customerId/sales/',
            query: c.type<PaginatedParameters>(),
            summary: 'Get sales of 1 customer',
            responses: {
                200: c.type<
                    PaginatedResponse<{
                        id: number
                        currency: string
                        date_updated: string
                        sale_id: string
                        sale_line_items: Array<{
                            id: number
                            sale: string
                            product_name: string | null
                            sku: string | null
                            num_sold: number
                            unit_price: string
                            total: string
                        }>
                        shop: {
                            shop_name: string
                            channel: {
                                channel_square_icon_url: string
                            }
                        }
                        total: string
                    }>
                >(),
            },
        },
    })
}

function createAddressContract() {
    const c = initContract()

    return c.router({
        detail: {
            method: 'GET',
            path: '/addresses/:id/',
            pathParams: c.type<{ id: number }>(),
            summary: 'Get address detail',
            responses: {
                200: c.type<{
                    id: number
                    is_default_shipping_address: boolean
                    is_default_billing_address: boolean
                    is_default_invoice_address: boolean
                    address_type: string | null
                    location_type: string | null
                    contact_name: string
                    address1: string
                    address2: string
                    country: string
                    province: string
                    city: string
                    district: string
                    post_code: string
                    phone: string
                }>(),
            },
        },
    })
}

function createConversationsContract() {
    const c = initContract()

    const Conversation = z.object({
        id: z.number(),
        store: z.number(),
        customer: z.number().nullable(),
        agent: z.number().nullable(),
        conversation_id: z.string(), // UUID
        date_created: z.string(),
        date_updated: z.string(),
        data: z.record(z.any()).nullable(),
    })

    const Turn = z.object({
        id: z.number(),
        conversation: z.number(),
        sender: z.number(),
        content: z.string(),
        timestamp: z.string(),
        data: z.record(z.any()).nullable(),
    })
    return c.router({
        list: {
            method: 'GET',
            path: '/stores/:storeId/conversations/',
            pathParams: c.type<{ storeId: string }>(),
            summary: 'List conversations for a store',
            responses: {
                200: z.array(Conversation),
            },
        },
        turns: {
            method: 'GET',
            path: '/conversations/:conversationId/turns/',
            pathParams: c.type<{ conversationId: number }>(),
            query: c.type<{ timestamp_gte?: string; timestamp_lte?: string }>(),
            summary: 'List turns for a conversation',
            responses: {
                200: z.array(Turn),
            },
        },
    })
}

function createDiscountContract() {
    const c = initContract()

    return c.router({
        updateChannelStockUnitDiscount: {
            method: 'POST',
            path: '/promotions/discounts/:discount_pk/products/update',
            pathParams: c.type<{ discount_pk: number }>(),
            description: 'Update channel stock unit discounts',
            body: c.type<{
                channel_stock_unit_discounts?: Array<{
                    stock_unit_id: number
                    shop_id: number
                    price?: number | null
                }>
                deleted_product_ids?: number[]
            }>(),
            responses: {
                200: c.type<{}>(),
            },
        },
    })
}

/**
 * Creates a Zod object schema with an `error` field and an optional `request_id` field.
 *
 * @param [error] - The Zod schema for the `error` field. Defaults to `z.string()`.
 *
 * @returns A Zod object schema with the specified `error` field and an optional `request_id` field.
 */
function makeGenericError<T extends z.ZodTypeAny = z.ZodString>(
    error = z.string() as unknown as T
) {
    return z.object({
        error,
        request_id: z.string().uuid().optional(),
    })
}

export type InitShippingInfoFieldsResponse = ClientInferResponseBody<
    ApiContract['sales']['initShippingInfoFields']
>
export type ProductDetailResponse = ClientInferResponses<ApiContract['products']['detail']>
export type ApiResponses = ClientInferResponses<ApiContract>

export type InferResponseBody<
    Route extends AppRoute,
    Status extends number = 200,
> = ClientInferResponseBody<Route, Status>

export function useApiQueryClient() {
    return useTsRestQueryClient(api)
}
